import { FC } from 'react';
import { ArrowLeft, User, Users, CircleOff } from 'lucide-react';

export type EndScope = 'this' | 'all';

interface Props {
  /** Current class label, shown on the "This class only" option. */
  className: string;
  roadmapName: string;
  /** Students who would be un-focused for each scope (informational counts). */
  thisClassCount: number;
  allClassesCount: number;
  /** Number of classes sharing this focus; the "All classes" option only shows when > 1. */
  classCount: number;
  scope: EndScope;
  onScopeChange: (scope: EndScope) => void;
  onBack: () => void;
  onKeepFocus: () => void;
  onEnd: () => void;
  ending?: boolean;
  /** Shown above the footer when an end attempt fails. */
  errorText?: string;
}

const LINK =
  'tw-bg-transparent tw-border-none tw-py-1 tw-text-indigo-600 tw-font-atkinson tw-font-bold tw-text-sm tw-leading-5 tw-cursor-pointer tw-inline-flex tw-items-center tw-gap-1';

const Radio: FC<{ checked: boolean }> = ({ checked }) => (
  <span
    aria-hidden
    className={`tw-size-[22px] tw-shrink-0 tw-rounded-full tw-inline-flex tw-items-center tw-justify-center tw-box-border tw-border-2 tw-border-solid ${
      checked ? 'tw-border-[#dc2626]' : 'tw-border-neutral-700'
    }`}
  >
    {checked && <span className="tw-size-2.5 tw-rounded-full tw-bg-[#dc2626]" />}
  </span>
);

export const EndFocusStep: FC<Props> = ({
  className,
  roadmapName,
  thisClassCount,
  allClassesCount,
  classCount,
  scope,
  onScopeChange,
  onBack,
  onKeepFocus,
  onEnd,
  ending = false,
  errorText,
}) => {
  const optionCard = (value: EndScope, title: string, detail: string, Icon: typeof User, ariaLabel: string) => {
    const selected = scope === value;
    return (
      <button
        type="button"
        role="radio"
        aria-checked={selected}
        aria-label={ariaLabel}
        onClick={() => onScopeChange(value)}
        className={`tw-flex tw-items-center tw-gap-3 tw-w-full tw-text-left tw-py-3 tw-px-4 tw-rounded-xl tw-cursor-pointer tw-bg-white tw-border-solid tw-border-2 ${
          selected ? 'tw-border-[#f3b0b0]' : 'tw-border-neutral-200'
        }`}
      >
        <Radio checked={selected} />
        <span className="tw-flex-1 tw-min-w-0 tw-flex tw-flex-col tw-gap-0.5">
          <span className="tw-font-atkinson tw-font-bold tw-text-base tw-leading-5 tw-text-neutral-950">{title}</span>
          <span className="tw-font-atkinson tw-text-sm tw-leading-5 tw-text-neutral-700">{detail}</span>
        </span>
        <Icon size={20} className="tw-shrink-0 tw-text-neutral-700" aria-hidden />
      </button>
    );
  };

  return (
    <div className="tw-flex tw-flex-col">
      <div className="tw-border-b-2 tw-border-neutral-100 tw-pt-4 tw-px-4 tw-pb-3 tw-flex tw-flex-col tw-gap-2">
        <div className="tw-flex tw-items-center tw-gap-3">
          <button type="button" onClick={onBack} className={LINK}>
            <ArrowLeft className="tw-size-4" />
            Back
          </button>
          <span className="tw-font-sora tw-font-bold tw-text-base tw-leading-6 tw-text-neutral-950">
            End this focus?
          </span>
        </div>
        <span className="tw-font-atkinson tw-text-sm tw-leading-5 tw-text-neutral-700">
          Students return to the first unit with unfinished skills
        </span>
      </div>

      <div className="tw-p-4 tw-flex tw-flex-col tw-gap-2">
        <span className="tw-font-atkinson tw-font-bold tw-text-xs tw-leading-4 tw-uppercase tw-text-neutral-600">
          End for
        </span>
        {optionCard(
          'this',
          'This class only',
          `${thisClassCount} ${thisClassCount === 1 ? 'student' : 'students'} from ${className} · ${roadmapName}`,
          User,
          'This class only',
        )}
        {classCount > 1 &&
          optionCard(
            'all',
            `All ${classCount} classes`,
            `${allClassesCount} students across ${classCount} classes`,
            Users,
            `All ${classCount} classes`,
          )}
      </div>

      {errorText && (
        <p role="alert" className="tw-m-0 tw-px-4 tw-pb-3 tw-text-[#dc2626] tw-font-atkinson tw-text-sm tw-leading-5">
          {errorText}
        </p>
      )}

      <div className="tw-border-solid tw-border-t-2 tw-border-neutral-100 tw-py-3 tw-px-4 tw-flex tw-items-center tw-gap-3">
        <button
          type="button"
          onClick={onKeepFocus}
          className="tw-shrink-0 tw-bg-white tw-text-indigo-600 tw-border-solid tw-border-2 tw-border-indigo-600 tw-rounded-full tw-py-3 tw-px-6 tw-font-atkinson tw-font-bold tw-text-base tw-leading-5 tw-cursor-pointer"
        >
          Keep focus
        </button>
        <button
          type="button"
          onClick={onEnd}
          disabled={ending}
          className={`tw-flex-1 tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-bg-[#dc2626] tw-text-white tw-border-solid tw-border-2 tw-border-[#dc2626] tw-rounded-full tw-py-3 tw-px-6 tw-font-atkinson tw-font-bold tw-text-base tw-leading-5 ${
            ending ? 'tw-cursor-not-allowed tw-opacity-60' : 'tw-cursor-pointer tw-opacity-100'
          }`}
        >
          <CircleOff size={18} aria-hidden />
          {scope === 'all' ? 'End for all classes' : 'End for 1 class'}
        </button>
      </div>
    </div>
  );
};
